import {
  Dialog,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import React from "react";
import { useHistory } from "react-router-dom";

import { deleteCreditEvaluation as deleteCreditEvaluationAPI } from "api/creditEvaluations";
import { creditEvaluationsKeys } from "api/creditEvaluations/queries";
import { CreditEvaluationsType } from "api/creditEvaluations/types";
import UltimateButton from "components/UltimateButton";

interface DeleteCreditEvaluationModalProps {
  isOpen: boolean;
  handleClose: () => void;
  creditEvaluation: CreditEvaluationsType | undefined;
}

const DeleteCreditEvaluationModal = ({
  isOpen,
  handleClose,
  creditEvaluation,
}: DeleteCreditEvaluationModalProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const history = useHistory();

  const { mutateAsync: deleteCreditEvaluation, status } = useMutation(
    async (id: string) => {
      const res = await deleteCreditEvaluationAPI(id);
      await queryClient.invalidateQueries(creditEvaluationsKeys.list());
      return res.data;
    },
  );

  const handleDelete = async () => {
    if (!creditEvaluation?._id) return;
    await deleteCreditEvaluation(creditEvaluation._id, {
      onSuccess: () => {
        handleClose();
        history.push("/credit-evaluations");
      },
    });
  };

  const displayName =
    creditEvaluation?.customer?.firstName ||
    creditEvaluation?.customer?.lastName
      ? `${creditEvaluation?.customer?.firstName ?? ""} ${
          creditEvaluation?.customer?.lastName ?? ""
        }`.trim() + "'s evaluation"
      : "this credit evaluation";

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          py: 2,
          pb: 3,
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          spacing={0.5}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            p={1.5}
            sx={{
              bgcolor: (t) =>
                (t.palette as any).brandDanger?.secondary ?? "#FEE9E8",
              borderRadius: "50%",
            }}
          >
            <Trash2
              size={32}
              color={(theme.palette as any).brandDanger?.primary ?? "#EC1F3D"}
            />
          </Stack>
          <Typography variant="headingMD" fontWeight={600}>
            Delete credit evaluation
          </Typography>
          <Typography
            variant="textSM"
            textAlign="center"
            sx={{
              color: (t) =>
                (t.palette as any).brandText?.secondary ?? "#A2A8B6",
            }}
          >
            Are you sure you want to delete{" "}
            <span
              style={{
                color: (theme.palette as any).brandText?.primary ?? "#111726",
              }}
            >
              {displayName}
            </span>
            ? This removes records from Lendzee and HubSpot. This action cannot
            be undone.
          </Typography>
        </Stack>
      </DialogTitle>

      <Stack width="100%" alignItems="center" justifyContent="center">
        <UltimateButton
          text="Delete"
          color="brandDanger"
          Icon={
            Trash2 as React.ComponentType<{
              size?: number;
              className?: string;
              style?: React.CSSProperties;
            }>
          }
          onClick={handleDelete}
          loading={status === "loading"}
          disabled={!creditEvaluation?._id}
          sx={{ py: 1 }}
        />
      </Stack>
    </Dialog>
  );
};

export default DeleteCreditEvaluationModal;
